/* WRAPPED IN AN IIFE BECAUSE THE STOCK PAGE NOW LOADS EVERY APP AT ONCE (15/08/2026).
   These were four separate pages, so each could declare `escapeHtml`, `spinnerHtml`,
   `MONTHS` and friends at the top level harmlessly. On one page they share ONE global
   scope, and a second `const escapeHtml` is a SyntaxError that kills the WHOLE script -
   so `customElements.define` never ran and every tab after Stock check sat on its
   spinner for ever. Nothing here is used across files; the wrapper simply stops them
   seeing each other. Same shape as the `money` collision in the sync's inlined modules. */
(function () {
/**
 * One Stock page, one strip of tabs.
 *
 * The Staff menu used to carry four separate stock links (Jonathan, 15/08/2026): "I want
 * the staff menu to just have two items in it: Rota, Stock", with everything that was in
 * that menu becoming tabs instead. So this page holds every stock section at once and
 * shows one at a time.
 *
 * TABS ARE DISCOVERED, NOT LISTED, and that is what makes the per-person part work. Each
 * section already decides for itself whether this customer may see it: a manager gets the
 * Stock overview app, a regular gets a paragraph explaining they may not. So a tab exists
 * only when its APP ELEMENT is actually in the page - "users that don't have access to
 * stock overview won't see the problems, sections or high demand tabs" falls out of that
 * rather than being a second copy of the permission rules that could drift from the first.
 *
 * THE STOCK OVERVIEW'S THREE VIEWS ARE PROMOTED to the top strip, because he asked for
 * them by name. They are that app's own internal tabs; rather than rewrite it, choosing
 * one here shows its panel and presses the button it already has. One set of tab state,
 * in the place that owns it.
 */

// Order matters: this is the order they appear, and the first one available is the one
// that opens. Stock check first because it is the thing most people are here to do.
// EACH TAB OPENS AT ITS WIDTH, BEFORE ANYTHING HAS LOADED (Jonathan, 15/10/2026): "work out what
// the size should be before it loads, and then try not to mess with it after that." These are the
// widths each tab measured at its widest on 15/10/2026, in rem (1rem = 10px here): Stock check in
// Accessories / Card Folders & Pages, whose ~100-character names set it; Pre-Orders and Deliveries
// by the many-columned supplier table; High demand by its longest TCG name. Each time new content
// loads, `holdWidth` checks it still fits and widens only if it does not, so these should rarely
// move. Re-measure and change a number here if a tab keeps widening.
const WIDTH = {
  check: 130,
  processing: 105,
  reservations: 105,
  preorders: 155,
  onsale: 105,
  deliveries: 155,
  problems: 130,
  sections: 85,
  demand: 110,
  backorders: 80,
};

// The most a tab will ever widen itself to, in rem. Long running text (a problem's detail) would
// otherwise always "need" the whole window.
const WIDEST = 160;

/**
 * THE OPEN TAB LOADS FIRST, THEN EVERY OTHER TAB LOADS BEHIND IT, ONE AT A TIME (Jonathan,
 * 15/10/2026): "the current tab it's on loads before anything else. As soon as it's loaded, the
 * background should start loading the other tabs", so that once everything is in, switching tabs
 * waits for nothing. Loading every tab at once queued six n8n calls behind each other and made
 * all of them slow; loading only on opening made every first click wait. One at a time, open tab
 * first, gets both.
 *
 * Each app hands its first load to `dcWhenShown`. Opening a tab that has not loaded yet jumps
 * the queue. A page with no tab strip (the old single-purpose pages) loads at once, and so does
 * everything if the strip never starts.
 */
const LOADERS = [];
let backgroundRunning = false;

/**
 * ONE FETCH OF EACH LIST PER PAGE, SHARED BY EVERY TAB THAT WANTS IT (Jonathan, 15/10/2026).
 * Four tabs each asked for the pre-orders list, two for the deliveries list and two for the
 * categories, with identical answers - about 15 of the page's 45 seconds. A READ asked again
 * while the first is still on its way, or within `SHARE_MS` of it, gets a copy of the same
 * answer. Who is asking (`staffName`, `by`) is left out of the match, because a list does not
 * depend on it. ANY OTHER call is a change, so it forgets every shared answer: a list read
 * after a write is always fresh. A failed answer is never shared.
 */
const SHARED_READS = new Set([
  'list', 'listCategories', 'listHighDemand', 'listHighDemandSections', 'releaseDates',
]);
const SHARE_MS = 30000;
const shared = new Map();

window.dcFetch = function dcFetch(url, init) {
  let body = null;
  try { body = JSON.parse((init && init.body) || ''); } catch (err) { body = null; }
  if (!body || !SHARED_READS.has(body.operation)) {
    // Another read (a search, the problems list, a picture lookup) changes nothing, so the
    // shared answers stand; anything else is treated as a change.
    const op = String((body && body.operation) || '');
    if (!/^(list|search|find|get|read|image)/i.test(op)) shared.clear();
    return fetch(url, init);
  }
  const { staffName, by, ...rest } = body;
  const key = `${url}|${JSON.stringify(rest)}`;
  // A caller that must see the server's answer NOW (polling for a change to land, re-reading
  // before a write) passes `cache: 'no-store'`; its fresh answer then becomes the shared one.
  if (init && init.cache === 'no-store') shared.delete(key);
  let entry = shared.get(key);
  if (!entry || Date.now() - entry.at > SHARE_MS) {
    // The caller's abort signal is NOT passed on: other tabs may be waiting on this answer.
    // Each caller still stops waiting when its own signal fires.
    const { signal, ...plain } = init || {};
    const promise = fetch(url, plain).then((response) => {
      if (!response.ok) shared.delete(key);
      return response;
    }, (err) => { shared.delete(key); throw err; });
    entry = { at: Date.now(), promise };
    shared.set(key, entry);
  }
  const copy = entry.promise.then((response) => response.clone());
  const signal = init && init.signal;
  if (!signal) return copy;
  return new Promise((resolve, reject) => {
    const stop = () => reject(new DOMException('Aborted', 'AbortError'));
    if (signal.aborted) { stop(); return; }
    signal.addEventListener('abort', stop, { once: true });
    copy.then(resolve, reject);
  });
};

function runLoader(entry) {
  if (entry.started) return entry.promise;
  entry.started = true;
  // A load that never answers must not hold the queue for ever.
  entry.promise = Promise.race([
    Promise.resolve().then(entry.fn).catch(() => {}),
    new Promise((resolve) => setTimeout(resolve, 20000)),
  ]);
  return entry.promise;
}

// TWO TABS LOAD AT ONCE BEHIND THE OPEN ONE (15/10/2026). One at a time was right while several
// tabs asked for the same lists; now those are shared, two lanes halve the wait for the rest.
const BACKGROUND_LANES = 2;

async function loadInBackground() {
  if (backgroundRunning) return;
  backgroundRunning = true;
  const lane = async () => {
    for (;;) {
      const next = LOADERS.find((e) => !e.started);
      if (!next) return;
      await runLoader(next);
    }
  };
  try {
    await Promise.all(Array.from({ length: BACKGROUND_LANES }, lane));
  } finally {
    backgroundRunning = false;
  }
}

function loadShown() {
  const shown = LOADERS.filter((e) => !e.host.hidden);
  if (!shown.length) return;
  Promise.all(shown.map(runLoader)).then(loadInBackground);
}

window.dcWhenShown = function dcWhenShown(el, fn) {
  const host = el.closest('.shopify-section');
  if (!host || !document.querySelector('stock-tabs')) { fn(); return; }
  LOADERS.push({ host, fn, started: false, promise: null });
  if (window.dcStockTabsReady) loadShown();
};

document.addEventListener('dc:stock-tab-shown', loadShown);
setTimeout(() => { if (!window.dcStockTabsReady) LOADERS.forEach(runLoader); }, 10000);

const PANELS = [
  { key: 'check', label: 'Stock check', selector: 'stock-check' },
  { key: 'processing', label: 'Arrivals', selector: 'stock-processing' },
  // NEXT TO STOCK PROCESSING, where he asked for it - the two are the same job seen from
  // either end: processing books stock in and can reserve some of it on the way, this
  // shows everything currently spoken for.
  { key: 'reservations', label: 'Reservations', selector: 'stock-reservations' },
  // Beside Reservations because they are the same question asked twice: what stock is
  // spoken for, and what stock is still coming.
  { key: 'preorders', label: 'Pre-Orders', selector: 'stock-preorders' },
  // Shift staff (01/11/2026): website orders still waiting, by product, with Fulfil and printed lists.
  { key: 'orders', label: 'Orders', selector: 'stock-orders' },
  // Supervisors and managers only (15/10/2026): marking down changes the till and website price.
  { key: 'onsale', label: 'On Sale', selector: 'stock-on-sale' },
  // Managers only (23/10/2026): what is on backorder with Asmodee, allocated and normal.
  { key: 'backorders', label: 'Backorders', selector: 'stock-backorders' },
  { key: 'deliveries', label: 'Deliveries', selector: 'deliveries-app' },
  { key: 'problems', label: 'Problems', selector: 'stock-check-manager', inner: 'problems' },
  { key: 'sections', label: 'Categories', selector: 'stock-check-manager', inner: 'sections' },
  { key: 'demand', label: 'High demand', selector: 'stock-check-manager', inner: 'demand' },
];

class StockTabs extends HTMLElement {
  connectedCallback() {
    this.strip = this.querySelector('[data-strip]');
    // The sections render after this one in the page, so wait for the document rather
    // than racing it. `DOMContentLoaded` has usually already fired by the time a deferred
    // script runs, hence the readyState check.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.build());
    } else {
      this.build();
    }
  }

  build() {
    this.tabs = [];
    this.pending = [];
    this.current = null;
    const claimed = new Map();

    for (const panel of PANELS) {
      const app = document.querySelector(panel.selector);
      if (!app) continue;                     // not rendered for this person: no tab
      const host = app.closest('.shopify-section') || app.parentElement;
      if (!host) continue;
      claimed.set(panel.key, { ...panel, app, host });
      this.tabs.push(claimed.get(panel.key));
    }

    // SOMEBODY WITH NO ACCESS AT ALL MUST NOT GET AN EMPTY PAGE. A signed-out visitor,
    // or a cleaner, has no tabs - and hiding the sections as well would leave a heading
    // and nothing else, which reads as broken rather than as "not for you". In that case
    // the sections keep their own notices ("please log in", "speak to Jonathan") and only
    // the empty tab strip goes.
    if (!this.tabs.length) {
      this.hidden = true;
      return;
    }

    // Every stock section this person CANNOT use renders a short notice instead of its
    // app. With no tab to reach it, that notice would sit on the page as loose prose
    // under whichever tab happened to be open, so it is hidden outright.
    document.querySelectorAll('.shopify-section').forEach((host) => {
      if (host.contains(this)) return;
      const isStock = host.querySelector(
        '.stock-check, .stock-processing, .deliveries, .stock-manager, .stock-on-sale, .stock-backorders',
      );
      if (!isStock) return;
      if (![...claimed.values()].some((t) => t.host === host)) host.hidden = true;
    });

    this.strip.innerHTML = '';
    this.tabs.forEach((tab) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'stock-tabs__tab';
      button.textContent = tab.label;
      button.dataset.key = tab.key;
      button.setAttribute('role', 'tab');
      button.addEventListener('click', () => this.show(tab.key));
      this.strip.appendChild(button);
      tab.button = button;
    });

    // Come back to where you were. Staff bounce between counting stock and booking a
    // delivery in, and landing on the first tab every time is its own small tax.
    // Another tab asking to move the page (a section name on Categories opening Stock check).
    document.addEventListener('dc:stock-show-tab', (event) => {
      const key = event.detail && event.detail.key;
      if (this.tabs.some((t) => t.key === key)) this.show(key);
    });

    const remembered = sessionStorage.getItem('dc-stock-tab');
    const wanted = this.tabs.some((t) => t.key === remembered) ? remembered : this.tabs[0].key;
    this.show(wanted);
  }

  show(key) {
    const chosen = this.tabs.find((t) => t.key === key) || this.tabs[0];
    // What is chosen RIGHT NOW, so a repeat scheduled by an earlier choice can tell that
    // it has been overtaken and stand down.
    this.current = chosen.key;
    sessionStorage.setItem('dc-stock-tab', chosen.key);

    // Hosts, not panels: several tabs can share one host (the Stock overview's three
    // views live in a single section), so a host is visible if ANY of its tabs is chosen.
    const hosts = new Set(this.tabs.map((t) => t.host));
    hosts.forEach((host) => { host.hidden = host !== chosen.host; });

    this.holdWidth(chosen);
    window.dcStockTabsReady = true;
    document.dispatchEvent(new CustomEvent('dc:stock-tab-shown', { detail: { key: chosen.key } }));

    this.tabs.forEach((tab) => {
      const on = tab.key === chosen.key;
      tab.button.classList.toggle('is-on', on);
      tab.button.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.button.tabIndex = on ? 0 : -1;
    });

    // The Stock overview keeps its own tab state, so ask it to move rather than
    // duplicating the decision. Its buttons only exist once it has rendered, so a miss
    // here is not an error - it simply opens on whichever view it was already showing.
    //
    // IT RE-RENDERS ITSELF - on load, and whenever anything is settled - which can put it
    // back on the view it was showing before. So the request is repeated for a moment
    // rather than fired once and hoped for.
    //
    // EVERY PENDING REPEAT IS CANCELLED FIRST, and this is the whole fix for the flicking
    // (Jonathan, 15/08/2026): choosing Categories left timers running that were still
    // trying to press Categories, so choosing High demand a second later fought them and
    // the page swapped back and forth before settling. A repeat also checks it is still
    // the chosen tab before it does anything, so a stale one cannot win a race.
    this.pending.forEach(clearTimeout);
    this.pending = [];
    if (this.watcher) {
      this.watcher.disconnect();
      this.watcher = null;
    }
    if (chosen.inner) {
      // Returns true once the button EXISTS, so the caller can tell "asked for"
      // from "not there yet".
      const press = () => {
        if (this.current !== chosen.key) return true;
        const inner = chosen.app.querySelector(`[data-tab="${chosen.inner}"]`);
        if (!inner) return false;
        if (!inner.classList.contains('is-on')) inner.click();
        return true;
      };
      // A SHORT TAIL OF REPEATS, because the app re-renders itself and can go
      // back to the view it was showing before.
      const repeats = () => {
        this.pending.push(setTimeout(press, 60));
        this.pending.push(setTimeout(press, 600));
        this.pending.push(setTimeout(press, 2500));
      };
      // WAIT FOR THE BUTTON, DO NOT GUESS HOW LONG IT TAKES (17/08/2026). These
      // repeats used to be timed from the moment the tab was chosen, and the
      // Stock overview only draws its tabs after three network calls - measured
      // at 12.3 SECONDS on a real load, against a last attempt at 2.5. So every
      // press missed, and choosing Categories or High demand left the app on
      // whichever view it defaults to: the tab said Categories and the page
      // showed the Problems table. Anchoring the repeats to the button actually
      // appearing is what makes it hold, however slow the load is.
      if (press()) {
        repeats();
      } else {
        const watcher = new MutationObserver(() => {
          if (this.current !== chosen.key) {
            watcher.disconnect();
            this.watcher = null;
            return;
          }
          if (!press()) return;
          watcher.disconnect();
          this.watcher = null;
          repeats();
        });
        watcher.observe(chosen.app, { childList: true, subtree: true });
        this.watcher = watcher;
      }
    }
  }
}

/**
 * Give the chosen tab its width: its measured width from `WIDTH`, at once, before anything loads -
 * or wider, if its content has since needed more. The Stock overview's three views share one app,
 * so the width is kept per tab, not per app.
 */
StockTabs.prototype.holdWidth = function holdWidth(tab) {
  this.widths = this.widths || {};
  if (this.widthWatch) { this.widthWatch.disconnect(); this.widthWatch = null; }
  clearTimeout(this.widthTimer);
  // THE SECTION'S WRAPPER IS WHAT IS SIZED, so its heading and buttons move with the app.
  const app = tab.app;
  const box = app.closest('.page-width') || app;
  const key = tab.key;
  const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 10;
  // A TAB NOT IN `WIDTH` IS SIZED TO ITS CONTENT, NEVER TO A GUESS (23/10/2026): Backorders went
  // live without an entry and opened at a fallback 100rem, a fifth wider than anything in it. So a
  // new tab starts at its natural width and is held there by the check below; give it a number in
  // `WIDTH` once its content is known, so it stops shifting as it loads.
  if (!this.widths[key] && WIDTH[key]) this.widths[key] = Math.round(WIDTH[key] * rem);
  box.style.width = this.widths[key] ? this.widths[key] + 'px' : '';

  // STILL FITS? Checked whenever the content changes (a section opened, a list arrived), with
  // the width lifted for that instant so nothing is drawn in between. Widens only, never narrows.
  const check = () => {
    if (this.current !== key) return;
    box.style.width = '';
    const natural = box.getBoundingClientRect().width;
    const want = Math.min(Math.round(natural), Math.round(WIDEST * rem));
    if (want > (this.widths[key] || 0)) this.widths[key] = want;
    box.style.width = this.widths[key] + 'px';
  };
  let quiet = null;
  this.widthWatch = new MutationObserver(() => { clearTimeout(quiet); quiet = setTimeout(check, 300); });
  this.widthWatch.observe(app, { childList: true, subtree: true });
};

customElements.define('stock-tabs', StockTabs);
})();
